import React, { useMemo, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BarChartOutlined, InboxOutlined, CarOutlined, TruckOutlined, FieldTimeOutlined, DownloadOutlined, ShopOutlined } from '@ant-design/icons';
import MainLayout from '../../../layouts/MainLayout';
import { findFirstAvailableMenuItem } from '../../../utils/menuUtils';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';

const DashboardLayout: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    const secondMenu = useMemo(() => [
        {
            key: 'scale',
            label: t('dashboard.scale.title'),
            icon: <BarChartOutlined />,
            display: hasResource([ResourceCode.data.SCALE_DATA]),
            onClick: () => navigate('/dashboard/scale'),
        },
        {
            key: 'pickup',
            label: t('dashboard.pickup.title'),
            icon: <ShopOutlined />,
            display: hasResource([ResourceCode.data.PICKUP_DATA]),
            onClick: () => navigate('/dashboard/pickup'),
        },
        {
            key: 'sorting',
            label: t('dashboard.sorting.title'),
            icon: <InboxOutlined />,
            display: hasResource([ResourceCode.data.SORTING_DATA]),
            onClick: () => navigate('/dashboard/sorting'),
        },
        {
            key: 'transportation',
            label: t('dashboard.transportation.title'),
            icon: <TruckOutlined />,
            display: hasResource([ResourceCode.data.TRANSPORTATION_DATA]),
            onClick: () => navigate('/dashboard/transportation'),
        },
        {
            key: 'delivery-submenu',
            label: t('dashboard.lastmile.title'),
            icon: <CarOutlined />,
            display: hasResource([ResourceCode.data.DELIVERY_DATA_MENU]),
            children: [
                {
                    key: 'delivery',
                    label: t('dashboard.lastmile.title'),
                    display: hasResource([ResourceCode.data.DELIVERY_DATA]),
                    onClick: () => navigate('/dashboard/delivery'),
                },
                {
                    key: 'daily-meeting',
                    label: t('dashboard.dailyMeeting.title'),
                    display: hasResource([ResourceCode.data.DAILY_MEETING_DATA]),
                    onClick: () => navigate('/dashboard/daily-meeting'),
                },
            ],
        },
        {
            key: 'quality',
            label: t('dashboard.quality.title'),
            icon: <FieldTimeOutlined />,
            display: hasResource([ResourceCode.data.QUALITY_DATA]),
            onClick: () => navigate('/dashboard/quality'),
        },
        {
            key: 'download',
            label: t('central.download.title'),
            icon: <DownloadOutlined />,
            display:
                hasResource([ResourceCode.data.SCALE_DATA]) ||
                hasResource([ResourceCode.data.PICKUP_DATA]) ||
                hasResource([ResourceCode.data.SORTING_DATA]) ||
                hasResource([ResourceCode.data.TRANSPORTATION_DATA]) ||
                hasResource([ResourceCode.data.DELIVERY_DATA]) ||
                hasResource([ResourceCode.data.QUALITY_DATA]),
            onClick: () => navigate('/dashboard/download'),
        },
    ], [t, navigate]);

    useEffect(() => {
        const isRoot = location.pathname === '/dashboard' || location.pathname === '/dashboard/';
        if (!isRoot) return;
        const first = findFirstAvailableMenuItem(secondMenu);
        if (first?.onClick) first.onClick();
    }, [location.pathname, secondMenu]);

    return (
        <MainLayout
            activeFirstMenu="dashboard"
            secondMenu={secondMenu}
        >
            <Outlet />
        </MainLayout>
    );
};

export default DashboardLayout;
